import React, { useState } from 'react'
import { PHASES, ARCH_CARDS, TOTAL_TASKS, type Phase, type Task } from './goalsData'
import { useGoalsStore } from './useGoalsStore'

const TAG_STYLES: Record<string, string> = {
  TypeScript: 'tag--ts',
  React:      'tag--react',
  Electron:   'tag--electron',
  WebSocket:  'tag--ws',
  AI:         'tag--ai',
  Voice:      'tag--voice',
  'VS Code':  'tag--vscode',
  Infra:      'tag--infra',
  UI:         'tag--ui',
  Launch:     'tag--launch',
}

const PHASE_ACCENT: Record<Phase['color'], string> = {
  accent:  'var(--accent)',
  green:   'var(--accent-2)',
  orange:  'var(--accent-3)',
  red:     'var(--red)',
  neutral: 'var(--text-1)',
}

const DIFF_COLORS: Record<string, { bg: string; color: string }> = {
  Easy:   { bg: 'rgba(95,212,164,0.12)', color: 'var(--accent-2)' },
  Medium: { bg: 'rgba(245,161,87,0.12)', color: 'var(--accent-3)' },
  Hard:   { bg: 'rgba(240,112,112,0.12)', color: 'var(--red)' },
}

export function GoalsPage() {
  const { checked, toggle, reset, completedCount, progressPct } = useGoalsStore()
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const done = completedCount()
  const pct = progressPct()

  const toggleCollapse = (id: string) =>
    setCollapsed((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })

  const handleReset = () => {
    if (window.confirm('Reset all progress? This cannot be undone.')) reset()
  }

  // Split phases into completed vs upcoming
  const completedPhases = PHASES.filter((p) => p.tasks.every((t) => !!checked[t.id]))
  const activePhases = PHASES.filter((p) => !p.tasks.every((t) => !!checked[t.id]))

  return (
    <div className="goals-page">
      {/* ── HEADER ── */}
      <div className="goals-header">
        <div className="goals-header__left">
          <div className="goals-header__logo">
            <span className="pulse-dot" style={{ width: 10, height: 10 }} />
            <span className="goals-header__logo-text">PULSE</span>
          </div>
          <h1 className="goals-header__title">
            BUILD <span className="goals-header__title-hl">ROADMAP</span>
          </h1>
          <p className="goals-header__sub">
            Every step to ship the IDE that beats Cursor. Tasks auto-check as we build them.
          </p>
        </div>

        {/* Progress card */}
        <div className="goals-progress-card">
          <div className="goals-progress-card__label">Overall Progress</div>
          <div className="goals-progress-card__pct" style={{ color: pct === 100 ? 'var(--accent-2)' : 'var(--accent)' }}>
            {pct}%
          </div>
          <div className="goals-progress-bar-wrap">
            <div
              className="goals-progress-bar"
              style={{ width: `${pct}%`, background: pct === 100 ? 'var(--accent-2)' : undefined }}
            />
          </div>
          <div className="goals-progress-card__sub">{done} of {TOTAL_TASKS} tasks done</div>
          <button className="goals-reset-btn" onClick={handleReset}>↺ Reset</button>
        </div>
      </div>

      {/* ── ARCHITECTURE ── */}
      <div className="goals-section-label">
        <span>Core Architecture</span>
        <span className="goals-section-line" />
      </div>
      <div className="goals-arch-grid">
        {ARCH_CARDS.map((c) => (
          <div key={c.name} className="goals-arch-card">
            <div className="goals-arch-card__icon">{c.icon}</div>
            <div className="goals-arch-card__name">{c.name}</div>
            <div className="goals-arch-card__detail">{c.detail}</div>
          </div>
        ))}
      </div>

      {/* ── UPCOMING SPRINTS ── */}
      {activePhases.length > 0 && (
        <>
          <div className="goals-sep" />
          <div className="goals-section-label">
            <span>🎯 Up Next</span>
            <span className="goals-section-line" />
          </div>
          {activePhases.map((phase) => (
            <PhaseBlock
              key={phase.id}
              phase={phase}
              checked={checked}
              collapsed={collapsed.has(phase.id)}
              onToggleCollapse={() => toggleCollapse(phase.id)}
              onToggleTask={toggle}
            />
          ))}
        </>
      )}

      {/* ── COMPLETED ── */}
      {completedPhases.length > 0 && (
        <>
          <div className="goals-sep" />
          <div className="goals-section-label">
            <span>✅ Completed</span>
            <span className="goals-section-line" />
          </div>
          {completedPhases.map((phase) => (
            <PhaseBlock
              key={phase.id}
              phase={phase}
              checked={checked}
              collapsed={collapsed.has(phase.id)}
              onToggleCollapse={() => toggleCollapse(phase.id)}
              onToggleTask={toggle}
            />
          ))}
        </>
      )}

      {/* ── FOOTER ── */}
      <div className="goals-footer">
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="pulse-dot" style={{ width: 6, height: 6 }} />
          <span>PULSE · Mission Control</span>
        </div>
        <span>Progress saved automatically</span>
      </div>
    </div>
  )
}

function PhaseBlock({
  phase, checked, collapsed, onToggleCollapse, onToggleTask,
}: {
  phase: Phase
  checked: Record<string, boolean>
  collapsed: boolean
  onToggleCollapse: () => void
  onToggleTask: (id: string) => void
}) {
  const phaseDone = phase.tasks.filter((t) => !!checked[t.id]).length
  const phaseColor = PHASE_ACCENT[phase.color]
  const allDone = phaseDone === phase.tasks.length

  return (
    <div className="goals-phase">
      <div className="goals-phase__header" onClick={onToggleCollapse}>
        <span
          className="goals-phase__badge"
          style={{ color: phaseColor, borderColor: `${phaseColor}50`, background: `${phaseColor}18` }}
        >
          {phase.label}
        </span>
        <span className="goals-phase__title">{phase.title}</span>
        {allDone && <span className="goals-phase__done-badge">✓ complete</span>}
        <span className="goals-phase__count" style={{ color: phaseColor }}>
          {phaseDone}/{phase.tasks.length}
        </span>
        <PhaseProgressBar done={phaseDone} total={phase.tasks.length} color={phaseColor} />
        <span className="goals-phase__chevron">{collapsed ? '▸' : '▾'}</span>
      </div>

      {!collapsed && (
        <div className="goals-tasks">
          {phase.tasks.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              checked={!!checked[task.id]}
              onToggle={() => onToggleTask(task.id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function TaskRow({ task, checked, onToggle }: { task: Task; checked: boolean; onToggle: () => void }) {
  const diff = task.difficulty ? DIFF_COLORS[task.difficulty] : null

  return (
    <div
      className={`goals-task ${checked ? 'goals-task--done' : ''}`}
      onClick={onToggle}
    >
      <div className={`goals-cb ${checked ? 'goals-cb--checked' : ''}`}>
        {checked && <span className="goals-cb__check">✓</span>}
      </div>

      <div className="goals-task__body">
        <div className="goals-task__name">{task.name}</div>
        <div className="goals-task__desc">{task.desc}</div>
        <div className="goals-task__tags">
          {task.tags.map((tag) => (
            <span key={tag} className={`goals-tag ${TAG_STYLES[tag] ?? 'tag--infra'}`}>
              {tag}
            </span>
          ))}
          {diff && (
            <span className="goals-tag" style={{ background: diff.bg, color: diff.color, borderColor: `${diff.color}40` }}>
              {task.difficulty}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

function PhaseProgressBar({ done, total, color }: { done: number; total: number; color: string }) {
  const pct = total > 0 ? (done / total) * 100 : 0
  return (
    <div className="goals-phase-bar">
      <div className="goals-phase-bar__fill" style={{ width: `${pct}%`, background: color }} />
    </div>
  )
}
